import type { ResolvedStateInfo } from '@blocksuite/affine-components/resource';
import { unsafeCSSVarV2 } from '@blocksuite/affine-shared/theme';
import { WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { BLOB_LOCAL_STORAGE_UNAVAILABLE_ERROR_CODE } from '@blocksuite/sync';
import { css, html } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

export const SURFACE_IMAGE_CARD_WIDTH = 220;
export const SURFACE_IMAGE_CARD_HEIGHT = 122;
export const NOTE_IMAGE_CARD_WIDTH = 752;
export const NOTE_IMAGE_CARD_HEIGHT = 78;

export class ImageBlockFallbackCard extends WithDisposable(ShadowlessElement) {
  static override styles = css`
    affine-image-fallback-card {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      user-select: none;
    }

    .affine-image-fallback-card {
      display: flex;
      flex: 1;
      gap: 8px;
      align-self: stretch;
      flex-direction: column;
      justify-content: space-between;
      border-radius: 8px;
      border: 1px solid ${unsafeCSSVarV2('layer/background/tertiary')};
      background: ${unsafeCSSVarV2('layer/background/secondary')};
      padding: 12px;
    }

    .truncate {
      align-self: stretch;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }

    .affine-image-fallback-card-title {
      display: flex;
      flex-direction: row;
      gap: 8px;
      align-items: center;
      align-self: stretch;
    }

    .affine-image-fallback-card-title-icon {
      display: flex;
      width: 16px;
      height: 16px;
      align-items: center;
      justify-content: center;
      color: var(--streamlain-v2-text-primary);
    }

    .affine-image-fallback-card-title-text {
      color: var(--streamlain-v2-text-placeholder);
      font-family: var(--streamlain-font-family);
      font-size: var(--streamlain-font-sm);
      font-style: normal;
      font-weight: 600;
      line-height: 22px;
    }

    .affine-image-fallback-card-description {
      color: var(--streamlain-v2-text-secondary);
      font-family: var(--streamlain-font-family);
      font-size: var(--streamlain-font-xs);
      font-style: normal;
      font-weight: 400;
      line-height: 20px;
    }

    .affine-image-fallback-card.loading {
      .affine-image-fallback-card-title {
        color: var(--streamlain-v2-text-placeholder);
      }
    }

    .affine-image-fallback-card.error {
      .affine-image-fallback-card-title-icon {
        color: ${unsafeCSSVarV2('status/error')};
      }
    }

    .affine-image-fallback-card.local-storage-unavailable {
      .affine-image-fallback-card-description {
        display: -webkit-box;
        overflow: hidden;
        white-space: normal;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
      }
    }
  `;

  override render() {
    const { icon, title, description, loading, error } = this.state;
    const localStorageUnavailable =
      this.state.errorCode === BLOB_LOCAL_STORAGE_UNAVAILABLE_ERROR_CODE;

    const classInfo = {
      'affine-image-fallback-card': true,
      'drag-target': true,
      'local-storage-unavailable': localStorageUnavailable,
      loading,
      error,
    };

    return html`
      <div class=${classMap(classInfo)}>
        <div class="affine-image-fallback-card-title">
          <div class="affine-image-fallback-card-title-icon">${icon}</div>
          <div class="affine-image-fallback-card-title-text truncate">
            ${title}
          </div>
        </div>
        <div
          class=${classMap({
            'affine-image-fallback-card-description': true,
            truncate: !localStorageUnavailable,
          })}
        >
          ${description}
        </div>
      </div>
    `;
  }

  @property({ attribute: false })
  accessor state!: ResolvedStateInfo;
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-image-fallback-card': ImageBlockFallbackCard;
  }
}
